import { t, tf } from "./i18n.js";
// Wohin Supabase nach dem Login zurückschicken soll.
//
// Ohne redirectTo nimmt Supabase die "Site URL" aus dem Dashboard — und die
// zeigt auf secure-darts.com. Beim Entwickeln landet man dadurch nach dem
// Login auf der Produktivseite statt auf localhost.
//
// Damit das greift, muss die Adresse zusätzlich in der Allow-Liste stehen:
// Supabase Dashboard -> Authentication -> URL Configuration -> Redirect URLs
function authRedirectUrl() {
  return window.location.origin + window.location.pathname;
}

async function handleGoogleLogin() {
  const { data, error } = await _supabase.auth.signInWithOAuth({
    provider: "google",
    options: { redirectTo: authRedirectUrl() },
  });

  if (error) {
    showToast(t("js.auth.019") + error.message, "error");
    console.error(error);
  }
  // Supabase leitet den Browser jetzt automatisch zu Google weiter.
  // Nach erfolgreichem Login springt er zurück, initAuth() erkennt den User und startet das Spiel!
}

async function handleRegister() {
  const email = document.getElementById("auth-email").value.trim();
  const password = document.getElementById("auth-password").value.trim();
  if (!email || !password)
    return showToast(t("js.auth.001"), "error");

  const { data, error } = await _supabase.auth.signUp({
    email,
    password,
    // Der Bestätigungslink in der Mail soll auf dieselbe Umgebung zeigen,
    // von der aus registriert wurde (Dev: localhost, sonst die Domain).
    options: { emailRedirectTo: authRedirectUrl() },
  });
  if (error)
    return showToast(t("js.auth.020") + error.message, "error");

  showToast(t("js.auth.002"), "success");
  currentUser = data.user;
  isGuest = false;
  initPresence();
  showMainApp();
}

function continueAsGuest() {
  isGuest = true;
  currentUser = null;
  showMainApp();
}

async function handleLogout() {
  // Beendet das Online-Tracking und verlässt den Channel
  if (presenceChannel) {
    await _supabase.removeChannel(presenceChannel);
    onlineUserIds.clear();
  }

  if (!isGuest) await _supabase.auth.signOut();
  isGuest = false;
  currentUser = null;
  document.getElementById("auth-email").value = "";
  document.getElementById("auth-password").value = "";
  showAuthScreen();
}

async function handleLogin() {
  const email = document.getElementById("auth-email").value.trim();
  const password = document.getElementById("auth-password").value.trim();
  if (!email || !password)
    return showToast(t("js.auth.001"), "error");

  const { data, error } = await _supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error)
    return showToast(t("js.auth.021") + error.message, "error");

  currentUser = data.user;
  isGuest = false;

  initPresence();
  showMainApp();
}

function changeUsername() {
  if (isGuest || !currentUser) {
    return showToast(t("js.auth.003"), "warning");
  }

  let currentName = currentUser.email.split("@")[0];
  if (currentUser.user_metadata && currentUser.user_metadata.display_name) {
    currentName = currentUser.user_metadata.display_name;
  } else if (currentUser.user_metadata && currentUser.user_metadata.full_name) {
    currentName = currentUser.user_metadata.full_name;
  }

  // FIX: prompt() blockiert den Browser und laesst sich nicht gestalten.
  showPromptModal(
    t("js.auth.004"),
    t("js.auth.005"),
    (value) => applyNewUsername(value, currentName),
    { value: currentName, placeholder: t("js.auth.022"), maxLength: 24 },
  );
}

async function applyNewUsername(newName, currentName) {
  if (newName && newName.trim() !== "" && newName !== currentName) {
    const cleanName = newName.trim();
    const lowerName = cleanName.toLowerCase();

    // 1. Prüfen, ob der Name ein verbotenes Wort enthält
    const forbiddenWords = [
      "admin",
      "administrator",
      "moderator",
      "system",
      "support",
    ];
    const isForbidden = forbiddenWords.some((word) => lowerName.includes(word));

    // 2. Wenn es ein verbotenes Wort ist, prüfen wir, ob der User wirklich Admin ist
    if (isForbidden) {
      // Admin-Status aus der DB holen (Passe den Tabellennamen an, falls nötig)
      const { data: adminData } = await _supabase
        .from("profiles")
        .select("is_admin")
        .eq("id", currentUser.id)
        .maybeSingle();

      const isRealAdmin = adminData && adminData.is_admin === true;

      // Nur blockieren, wenn er das Wort nutzt UND KEIN echter Admin ist
      if (!isRealAdmin) {
        return showToast(
          t("js.auth.006"),
          "error",
        );
      }
    }

    // 3. Wenn wir hier ankommen, ist alles okay (entweder sauberer Name oder echter Admin)
    const { data, error } = await _supabase.auth.updateUser({
      data: { display_name: cleanName },
    });

    if (error) {
      showToast(t("js.auth.007") + error.message, "error");
    } else {
      currentUser = data.user;
      showToast(t("js.auth.008") + cleanName, "success");
      if (typeof showMainApp === "function") showMainApp();
    }
  }
}

// ==========================================
// PASSWORT VERGESSEN & RESET LOGIK
// ==========================================

async function handleForgotPassword() {
  const email = document.getElementById("auth-email").value.trim();

  if (!email) {
    return showToast(
      t("js.auth.009"),
      "warning",
    );
  }

  const { data, error } = await _supabase.auth.resetPasswordForEmail(email, {
    // Leitet den Nutzer nach Klick auf den E-Mail-Link wieder auf die aktuelle URL der App zurück
    redirectTo: authRedirectUrl(),
  });

  if (error) {
    showToast(t("js.auth.010") + error.message, "error");
  } else {
    showToast(t("js.auth.011"), "success");
  }
}

// Globaler Listener, der lauscht, ob jemand über einen "Passwort Reset Link" in die App kommt
_supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === "PASSWORD_RECOVERY") {
    // Der User hat auf den Link in der Mail geklickt und ist jetzt temporär authentifiziert
    // FIX: prompt() zeigt Passwoerter im Klartext an. Das Modal nutzt
    // ein echtes type="password"-Feld.
    showPromptModal(
      t("js.auth.023"),
      t("js.auth.024"),
      (newPassword) => applyNewPassword(newPassword),
      { type: "password", placeholder: t("js.auth.neuesPasswort"), maxLength: 72 },
    );
    return;
  }
});

async function applyNewPassword(newPassword) {
  if (!newPassword || newPassword.length < 6) {
    showToast(
      t("js.auth.012"),
      "warning",
    );
    return;
  }

  // Neues Passwort in der Datenbank speichern
  const { data, error } = await _supabase.auth.updateUser({
    password: newPassword,
  });

  if (error) {
    showToast(
      t("js.auth.013") + error.message,
      "error",
    );
  } else {
    showToast(t("js.auth.025"), "success");
    // Danach ist der User normal eingeloggt
    currentUser = data.user;
    isGuest = false;
    if (typeof initPresence === "function") initPresence();
    if (typeof showMainApp === "function") showMainApp();
  }
}

function requestAccountDeletion() {
  showConfirmModal(
    t("js.auth.014"),
    async () => {
      if (!currentUser) return;

      // Button UI aktualisieren, damit der User merkt, dass etwas passiert.
      // BUGFIX 07.08.2026: Der Selektor verlangte die Klasse .reset - der
      // Knopf traegt aber .btn-glass. btn war deshalb immer null und die
      // "Loesche Account..."-Rueckmeldung erschien nie.
      const btn = document.querySelector(
        'button[onclick="requestAccountDeletion()"]'
      );
      if (btn) {
        btn.innerText = t("js.auth.015");
        btn.disabled = true;
      }

      try {
        // Die Edge Function aufrufen
        const { data, error } = await _supabase.functions.invoke(
          "delete-user",
          {
            method: "POST",
          }
        );

        if (error) {
          throw new Error(error.message);
        }

        showToast(t("js.auth.016"), "success");

        // Modal schließen und ausloggen (Cookie & Session löschen)
        // Einstellungen sind seit Juli 2026 ein Screen, kein Modal mehr
        document.getElementById("settings-screen")?.style.setProperty("display", "none");

        setTimeout(() => {
          handleLogout();
        }, 1500);
      } catch (err) {
        showToast(t("js.auth.017") + err.message, "error");
        if (btn) {
          btn.innerText = t("js.auth.018");
          btn.disabled = false;
        }
      }
    }
  );
}

// ---------------------------------------------------------------
// Bruecke zu window: Inline-Handler im HTML und andere Module greifen
// weiterhin global auf diese Funktionen zu. Automatisch erzeugt beim
// Umstieg auf Vite - Modul-Scope ersetzt sonst die alten Globals.
// ---------------------------------------------------------------
Object.assign(window, {
  handleGoogleLogin,
  handleRegister,
  continueAsGuest,
  handleLogout,
  handleLogin,
  changeUsername,
  handleForgotPassword,
  requestAccountDeletion,
});
